import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coins, TrendingDown, Calendar, RefreshCw, CreditCard, Settings, HelpCircle, Plus, X, Loader2, Crown, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Widget compacto para el Dashboard
export function CreditsWidget() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  
  if (!user) return null;
  
  const hasSubscription = user.plan && ['essentials_monthly', 'essentials_annual', 'plus_monthly', 'plus_annual'].includes(user.plan);
  
  return (
    <>
      <div className="glass rounded-xl p-4 border border-white/10" data-testid="credits-widget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Coins className="w-5 h-5 text-[#FF6B00]" />
            <span className="text-sm font-medium text-white">Créditos</span>
          </div>
          <button onClick={() => setShowModal(true)} className="p-1 rounded hover:bg-white/10 text-gray-400">
            <HelpCircle className="w-4 h-4" />
          </button>
        </div>
        
        <div className="text-3xl font-bold text-white mb-2">
          {(user.credits || 0).toFixed(2)}
        </div>
        
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
          {hasSubscription ? (
            <>
              <Crown className="w-3 h-3 text-[#FF6B00]" />
              <span className="text-[#FF6B00]">{user.plan?.includes('plus') ? 'Plus' : 'Essentials'}</span>
            </>
          ) : (
            <>
              <Zap className="w-3 h-3" />
              <span>Plan Gratuito</span>
            </>
          )}
        </div>
        
        <div className="space-y-2">
          <button
            onClick={() => navigate('/pricing')}
            className="w-full py-2 rounded-lg bg-[#FF6B00]/20 text-[#FF6B00] text-sm font-medium hover:bg-[#FF6B00]/30 flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            {hasSubscription ? 'Comprar créditos' : 'Suscribirse'}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="w-full py-2 rounded-lg bg-white/5 text-gray-400 text-sm hover:bg-white/10 flex items-center justify-center gap-2"
          >
            <Settings className="w-4 h-4" />
            Gestionar
          </button>
        </div>
      </div>
      
      <CreditsModal isOpen={showModal} onClose={() => setShowModal(false)} />
    </>
  );
}

// Modal completo de gestión de créditos
export function CreditsModal({ isOpen, onClose }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  
  useEffect(() => {
    if (isOpen && user) {
      fetchData();
    }
  }, [isOpen, user]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, histRes] = await Promise.all([
        axios.get(`${API}/subscription`),
        axios.get(`${API}/payments/history`)
      ]);
      setSubscription(subRes.data);
      setTransactions(histRes.data.transactions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const cancelSubscription = async () => {
    if (!window.confirm('¿Cancelar tu suscripción? Mantendrás tus créditos pero no recibirás más.')) return;
    try {
      await axios.post(`${API}/subscription/cancel`);
      toast.success('Suscripción cancelada');
      await refreshUser();
      fetchData();
    } catch (e) {
      toast.error('Error al cancelar');
    }
  };
  
  if (!isOpen) return null;
  
  const hasSubscription = user?.plan && ['essentials_monthly', 'essentials_annual', 'plus_monthly', 'plus_annual'].includes(user.plan);
  const planName = user?.plan?.includes('plus') ? 'Plus' : user?.plan?.includes('essentials') ? 'Essentials' : 'Gratuito';
  const isAnnual = user?.plan?.includes('annual');
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-lg glass rounded-2xl overflow-hidden max-h-[85vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="credits-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#FF6B00]/20">
                <Coins className="w-5 h-5 text-[#FF6B00]" />
              </div>
              <h2 className="text-lg font-bold text-white">Gestión de Créditos</h2>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'overview' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
            >
              Resumen
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'history' ? 'text-[#FF6B00] border-b-2 border-[#FF6B00]' : 'text-gray-400 hover:text-white'}`}
            >
              Historial
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#FF6B00]" />
              </div>
            ) : activeTab === 'overview' ? (
              <div className="space-y-5">
                {/* Credits display */}
                <div className="glass rounded-xl p-5 border border-white/10 text-center">
                  <p className="text-sm text-gray-400 mb-1">Créditos disponibles</p>
                  <p className="text-4xl font-bold text-white">{(user?.credits || 0).toFixed(2)}</p>
                </div>
                
                {/* Subscription status */}
                <div className="glass rounded-xl p-4 border border-white/10">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-400">Plan actual</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      hasSubscription ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {hasSubscription ? 'Activo' : 'Sin suscripción'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${hasSubscription ? 'bg-[#FF6B00]/20' : 'bg-white/5'}`}>
                      {hasSubscription ? <Crown className="w-5 h-5 text-[#FF6B00]" /> : <Zap className="w-5 h-5 text-gray-500" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{planName}</p>
                      {hasSubscription && (
                        <p className="text-xs text-gray-500">{isAnnual ? 'Anual' : 'Mensual'}</p>
                      )}
                    </div>
                  </div>
                  
                  {subscription?.subscription_ends && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      <span>Próxima renovación: {new Date(subscription.subscription_ends).toLocaleDateString('es')}</span>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="space-y-2">
                  <button
                    onClick={() => { onClose(); navigate('/pricing'); }}
                    className="w-full py-3 rounded-xl bg-[#FF6B00] text-white font-medium hover:bg-[#FF8533] flex items-center justify-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    {hasSubscription ? 'Comprar más créditos' : 'Ver planes'}
                  </button>
                  
                  {hasSubscription && (
                    <button
                      onClick={cancelSubscription}
                      className="w-full py-2 rounded-lg border border-red-500/30 text-red-400 text-sm hover:bg-red-500/10"
                    >
                      Cancelar suscripción
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.length === 0 ? (
                  <div className="text-center py-8">
                    <CreditCard className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                    <p className="text-gray-500">Sin transacciones</p>
                  </div>
                ) : (
                  transactions.slice(0, 10).map((t, i) => (
                    <div key={t.id || i} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${t.payment_type === 'subscription' ? 'bg-purple-500/20' : 'bg-green-500/20'}`}>
                          {t.payment_type === 'subscription' ? <Crown className="w-4 h-4 text-purple-400" /> : <Plus className="w-4 h-4 text-green-400" />}
                        </div>
                        <div>
                          <p className="text-sm text-white">{t.item_name || t.payment_type}</p>
                          <p className="text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString('es')}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-white">${t.amount}</p>
                        <p className={`text-xs ${t.payment_status === 'paid' ? 'text-green-400' : 'text-yellow-400'}`}>
                          {t.payment_status === 'paid' ? 'Pagado' : 'Pendiente'}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default CreditsWidget;
